import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';

import { OUTREACH_ANALYTICS_TIMEZONE } from '@apps/web/components/admin/outreach_observability/constants';
import { Badge } from '@apps/web/components/ui/badge';
import { type KpiRunListItem } from '@libraries/typescript/api_client';

function formatCreatedAt(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'M/d h:mm a',
    );
}

function formatCostUsd(cost: number | null): string {
    return cost === null ? '—' : `$${cost.toFixed(2)}`;
}

function formatDurationMs(durationMs: number | null): string {
    if (durationMs === null) {
        return '—';
    }
    const seconds = durationMs / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(0)}s`;
    }
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${Math.round(seconds % 60)}s`;
}

export function KpiRunsTable({
    runs,
    totalMatching,
    truncated,
    isLoading,
    isError,
}: {
    readonly runs: Array<KpiRunListItem>;
    readonly totalMatching: number;
    readonly truncated: boolean;
    readonly isLoading: boolean;
    readonly isError: boolean;
}) {
    // Only surface loading/error/empty placeholders when there are no rows to
    // show. Once runs have loaded, keep rendering them even if a later
    // background revalidation is in flight or errored (SWR keeps the last data),
    // so a transient blip doesn't drop valid rows.
    if (runs.length === 0) {
        if (isLoading) {
            return (
                <div className="text-muted-foreground py-6 text-center text-sm">
                    Loading runs…
                </div>
            );
        }
        if (isError) {
            return (
                <div className="text-destructive py-6 text-center text-sm">
                    Failed to load runs.
                </div>
            );
        }
        return (
            <div className="text-muted-foreground py-6 text-center text-sm">
                No runs in this window.
            </div>
        );
    }
    return (
        <div className="flex flex-col gap-2">
            <div className="text-muted-foreground text-xs">
                {truncated
                    ? `Showing ${runs.length} of ${totalMatching} runs (newest first)`
                    : `${totalMatching} runs`}
            </div>
            <div className="max-h-80 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                        <tr className="text-muted-foreground text-left">
                            <th className="px-3 py-2 font-medium">Time</th>
                            <th className="px-3 py-2 font-medium">Org</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                            <th className="px-3 py-2 font-medium">Shifts</th>
                            <th className="px-3 py-2 text-right font-medium">
                                Cost
                            </th>
                            <th className="px-3 py-2 text-right font-medium">
                                Duration
                            </th>
                            <th className="px-3 py-2 font-medium">Links</th>
                        </tr>
                    </thead>
                    <tbody>
                        {runs.map((run) => (
                            <tr key={run.run_id} className="border-t">
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                    {formatCreatedAt(run.created_at)}
                                </td>
                                <td className="px-3 py-2">{run.org_name}</td>
                                <td className="px-3 py-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                        <Badge variant="secondary">
                                            {run.outreach_status}
                                        </Badge>
                                        {run.is_stale_idle ? (
                                            <Badge variant="destructive">
                                                stale
                                            </Badge>
                                        ) : null}
                                        {run.compaction_count > 0 ? (
                                            <Badge variant="outline">
                                                compaction ×
                                                {run.compaction_count}
                                            </Badge>
                                        ) : null}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                                    {run.covered_shift_count} cov ·{' '}
                                    {run.yes_shift_count} yes ·{' '}
                                    {run.filled_shift_count} fill
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatCostUsd(run.total_cost_usd)}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {formatDurationMs(run.duration_ms)}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        {run.agent_conversation_id ? (
                                            <Link
                                                to="/admin/agent-traces/$conversationId"
                                                params={{
                                                    conversationId:
                                                        run.agent_conversation_id,
                                                }}
                                                className="text-primary hover:underline"
                                            >
                                                Trace
                                            </Link>
                                        ) : null}
                                        <Link
                                            to="/outreaches/$callout-id"
                                            params={{
                                                'callout-id': run.outreach_id,
                                            }}
                                            search={{
                                                organizationId:
                                                    run.organization_id,
                                                mode: 'live',
                                            }}
                                            className="text-primary hover:underline"
                                        >
                                            Outreach
                                        </Link>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
